import React, { useEffect, useRef, useState } from 'react';
import type { Variants } from 'framer-motion';
import {
  ButtonContainer,
  ButtonTitle,
  ProfileButton,
  ProfileModal,
} from './profile.styled';
import { useAuth } from '../../context/auth-provider';
import { font, Text } from '../../style-utils/text';
import { User } from '../../interfaces/user-interfaces';
import { useNavigate } from 'react-router-dom';
import { SessionsPanel } from '../auth/sessions-panel';
import { ModalPortal } from '../modal/modal-portal';
import { ROLES, normalizeUserRole, isStaff } from '../../auth/roles';
import { PERMISSIONS } from '../../auth/permissions';
import { deskInPlan } from '../../utils/products';
import {
  DISTANCE,
  DURATION,
  EASE,
  Presence,
  SCALE,
  T,
  preset,
  tween,
} from '../../motion';

interface ProfileProps {
  dashboard?: boolean;
}

// Distance between the trigger and the panel, in px.
const GAP = 8;

/* THE POPOVER GROWS OUT OF ITS ANCHOR. On the dashboard the anchor is the
   avatar ABOVE the panel, so the shared `popover` preset (which starts a hair
   above its rest position and settles down) is right. On the desk rail the
   panel sits ABOVE the button, so it has to start a hair below and settle UP —
   the same numbers, the other sign. The transform-origin is set in the styled
   component per anchor. */
const popoverUp: Variants = {
  hidden: {
    opacity: 0,
    scale: SCALE.popoverFrom,
    y: DISTANCE.micro * 2,
    transition: tween(DURATION.instant, EASE.exit),
  },
  visible: { opacity: 1, scale: 1, y: 0, transition: T.micro },
};

export const Profile: React.FC<ProfileProps> = ({ dashboard }) => {
  const {
    user,
    permissions,
    logout,
  }: {
    user: User | null | undefined;
    permissions: string[];
    logout: (everywhere?: boolean) => Promise<void>;
  } = useAuth();
  const [showProfileModal, setShowProfileModal] = useState<boolean>(false);
  const [showDevices, setShowDevices] = useState<boolean>(false);
  const navigate = useNavigate();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  // Where the portalled panel sits. Measured from the trigger rather than
  // hardcoded, because once the panel is portalled to <body> it no longer has
  // the header as a containing block and `top: 72px` would mean "72px from the
  // viewport", not "below the avatar".
  const [anchor, setAnchor] = useState<{
    top?: number;
    bottom?: number;
    left?: number;
    right?: number;
  }>({});

  const handleProfile = () => {
    setShowProfileModal(!showProfileModal);
  };

  // Single shared logout (revokes this device's session server-side, clears the
  // httpOnly cookies, resets auth state and routes to login).
  const logOut = () => logout();

  // Keep the panel glued to the avatar. A fixed element does not scroll with
  // the page, so without this it would visibly detach the moment the user
  // scrolls under an open menu.
  useEffect(() => {
    if (!showProfileModal) {
      return undefined;
    }

    const place = () => {
      const rect = triggerRef.current?.getBoundingClientRect();

      if (!rect) {
        return;
      }

      // Dashboard: hangs below the avatar, right edges aligned — the same
      // relationship the old `top: 72px; right: 16px` produced, now derived.
      // Desk shell: sits above the bottom rail button, left edges aligned.
      setAnchor(
        dashboard
          ? {
              top: Math.round(rect.bottom + GAP),
              right: Math.round(window.innerWidth - rect.right),
            }
          : {
              bottom: Math.round(window.innerHeight - rect.top + GAP),
              left: Math.round(rect.left),
            }
      );
    };

    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);

    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [showProfileModal, dashboard]);

  useEffect(() => {
    if (!showProfileModal) {
      return undefined;
    }

    const frame = window.requestAnimationFrame(() => {
      popoverRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
    });
    const closeOnOutsidePress = (event: MouseEvent) => {
      const target = event.target as Node;

      if (
        !popoverRef.current?.contains(target) &&
        !triggerRef.current?.contains(target)
      ) {
        setShowProfileModal(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowProfileModal(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', closeOnOutsidePress);
    document.addEventListener('keydown', closeOnEscape);

    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener('mousedown', closeOnOutsidePress);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [showProfileModal]);

  return (
    <>
      <ProfileButton
        ref={triggerRef}
        type="button"
        dashboard={dashboard}
        aria-label="Open account menu"
        aria-haspopup="dialog"
        aria-expanded={showProfileModal}
        aria-controls={showProfileModal ? 'account-popover' : undefined}
        onClick={() => handleProfile()}
      >
        <Text
          as="span"
          mt={0}
          mb={0}
          ml={0}
          mr={0}
          fontFamily={font.family}
          fontSize={font.size.l}
          color={'white'}
        >
          {user?.full_name?.charAt(0)}
          {/* {user?.full_name.length > 0
            ? user?.full_name.charAt(0)
            : user?.property_ids[0]?.property_name} */}
        </Text>
      </ProfileButton>
      {/* The conditional lives inside the Presence so closing plays the exit
          (the panel shrinks back into the avatar) before the portal unmounts;
          a re-open mid-exit simply reverses. */}
      <Presence>
        {showProfileModal ? (
          <ModalPortal key="account-popover">
            <ProfileModal
              variants={dashboard ? preset.popover : popoverUp}
              initial="hidden"
              animate="visible"
              exit="hidden"
              ref={popoverRef}
              id="account-popover"
              role="dialog"
              aria-labelledby="account-popover-title"
              dashboard={dashboard}
              $top={anchor.top}
              $bottom={anchor.bottom}
              $left={anchor.left}
              $right={anchor.right}
            >
              <Text
                id="account-popover-title"
                fontFamily={font.family}
                fontWeight={font.weight.semibold}
                align={'center'}
                mt={0}
                mb={8}
              >
                {user?.full_name}
              </Text>
              {/* Staff only. A concierge has no dashboard, and since migration
                037 a resident has no staff role at all — before this was gated
                on isStaff they were offered a Dashboard button that 403s. */}
              {/* Independent Stays IS1: hidden without Lobbero Desk — there is
                no front desk to switch to, and the dashboard is Stays. */}
              {isStaff(user?.role) &&
                normalizeUserRole(user?.role) !== ROLES.CONCIERGE &&
                deskInPlan(user) && (
                  <ButtonContainer
                    type="button"
                    dashboard={dashboard}
                    onClick={() =>
                      navigate(dashboard ? '/main-menu' : '/dashboard', {
                        replace: false,
                      })
                    }
                  >
                    <ButtonTitle>
                      {dashboard ? 'Main Menu' : 'Dashboard'}
                    </ButtonTitle>
                  </ButtonContainer>
                )}
              {/* The other half of the dual hat. Someone who works here AND
                lives here holds portal:read alongside their staff role, and
                their post-auth home is the staff side — so without this the
                resident portal would be reachable only by typing the URL.
                Shown for a pure resident too, where it is simply the way back
                home. */}
              {permissions.includes(PERMISSIONS.PORTAL_READ) && (
                <ButtonContainer
                  type="button"
                  dashboard={dashboard}
                  onClick={() => navigate('/portal', { replace: false })}
                >
                  <ButtonTitle>Resident Portal</ButtonTitle>
                </ButtonContainer>
              )}
              {/* THE ENTRY THAT WAS MISSING. This menu offered Change Password,
                Devices and Sign Out — so there was nowhere in the product to
                correct your own name, phone number or email address, whatever
                your role. First in the group because it is the one people come
                here looking for; the other two are now sections of it.

                Every role, no gate. Editing your own account is not a staff
                capability, and /account is registered without a role or
                permission entry for the same reason. */}
              <ButtonContainer
                type="button"
                dashboard={dashboard}
                onClick={() => navigate('/account/profile', { replace: false })}
              >
                <ButtonTitle>Account</ButtonTitle>
              </ButtonContainer>
              <ButtonContainer
                type="button"
                dashboard={dashboard}
                onClick={() =>
                  navigate('/account/password', { replace: false })
                }
              >
                <ButtonTitle>Change Password</ButtonTitle>
              </ButtonContainer>
              {/* Still an in-place expander rather than a link. Checking "is
                anything signed in that shouldn't be" is a glance, and a glance
                that costs a page navigation away from whatever you were doing
                is a glance people stop taking. The same panel has a permanent
                home at /account/devices for the times it is the actual
                errand. */}
              <ButtonContainer
                type="button"
                dashboard={dashboard}
                aria-expanded={showDevices}
                onClick={() => setShowDevices(!showDevices)}
              >
                <ButtonTitle>
                  {showDevices ? 'Hide devices' : 'Devices'}
                </ButtonTitle>
              </ButtonContainer>
              {showDevices && <SessionsPanel />}
              {/* The policies (privacy, terms, cookies, DPA, security), every
                  role. Lands on the account page's Legal section, which lists
                  them; each opens at /legal/<doc>. */}
              <ButtonContainer
                type="button"
                dashboard={dashboard}
                onClick={() => navigate('/account/legal', { replace: false })}
              >
                <ButtonTitle>Legal &amp; Privacy</ButtonTitle>
              </ButtonContainer>
              <ButtonContainer
                type="button"
                dashboard={dashboard}
                onClick={() => logOut()}
              >
                <ButtonTitle>Sign Out </ButtonTitle>
              </ButtonContainer>
            </ProfileModal>
          </ModalPortal>
        ) : null}
      </Presence>
    </>
  );
};
